import { useState, type FormEvent } from 'react';
import { ErrorBox, Field, ThemeSelect } from '../../shared/ui';
import { PoweredBy, ProvaliaLogoStacked, ProvaliaMark } from '../../shared/brand';
import { api, type PortalInfo, type PortalSession } from '../api';

/**
 * El logo del cliente. Sin logo propio, el isotipo de Provalia y su nombre;
 * con `nameFallback={false}` (la entrada, que ya pone el nombre) no sale nada.
 */
export function TenantLogo({ info, className, nameFallback = true }: {
  info: { name: string; hasLogo: boolean };
  className?: string;
  nameFallback?: boolean;
}): JSX.Element | null {
  const [failed, setFailed] = useState(false);
  if (!info.hasLogo || failed) {
    if (!nameFallback) return null;
    return (
      <span className="pt-tenant">
        <ProvaliaMark size={28} />
        <span className="pt-tenant-name">{info.name}</span>
      </span>
    );
  }
  return <img className={className} src="/api/portal/logo" alt={info.name} onError={() => setFailed(true)} />;
}

export function Login({
  info,
  onLogin,
  onForgot,
  notice,
  initialEmail = '',
}: {
  info: PortalInfo;
  onLogin: () => void;
  onForgot: () => void;
  notice?: string | null;
  initialEmail?: string;
}): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ token: string; session: PortalSession }>('/portal/login', { email: email.trim(), password });
      api.setToken(res.token);
      onLogin();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="stack lg" style={{ width: '100%', maxWidth: 400 }}>
        <div className="card login-card">
          <div className="card-body">
            <div className="login-brand">
              <ProvaliaLogoStacked height={92} />
              <div className="pt-login-client">
                <TenantLogo info={info} className="pt-login-client-logo" nameFallback={false} />
                <h1 className="pt-login-title">{info.name}</h1>
                <div className="muted">Portal de proveedores</div>
              </div>
            </div>
            <form className="stack" onSubmit={(e) => void submit(e)}>
              {notice ? <div className="callout info">{notice}</div> : null}
              <Field label="Correo">
                <input
                  className="input pt-big"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </Field>
              <Field label="Contraseña">
                <input
                  className="input pt-big"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              <ErrorBox error={error} />
              <button className="btn primary block pt-big" type="submit" disabled={busy || !email || !password}>
                {busy ? 'Entrando…' : 'Entrar'}
              </button>
              <button className="btn ghost block" type="button" onClick={onForgot}>
                ¿Has olvidado tu contraseña?
              </button>
              <p className="subtle" style={{ textAlign: 'center' }}>
                ¿No tienes usuario? Pide a compras de {info.name} que te dé de alta: te llegará un correo para crear tu contraseña.
              </p>
            </form>
          </div>
        </div>
        <div className="row between">
          <PoweredBy />
          <ThemeSelect />
        </div>
      </div>
    </div>
  );
}

export function Unavailable({ name }: { name: string | null }): JSX.Element {
  return (
    <div className="login-page">
      <div className="card login-card">
        <div className="card-body stack" style={{ textAlign: 'center' }}>
          <div className="login-brand">
            <ProvaliaLogoStacked height={92} />
          </div>
          <h1>Portal no disponible</h1>
          <p className="muted">
            {name ? `El portal de proveedores de ${name}` : 'Este portal de proveedores'} no está disponible en este momento.
          </p>
          <p className="muted">Si necesitas algo urgente sobre un pedido, contacta directamente con su departamento de compras.</p>
          <div style={{ marginTop: 8 }}>
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
}
